import type { IQuantityDisplay } from "@/common/types/quantity";
import { formatQuantity, formatQuantityPart } from "@/common/utils/quantity";

interface IQuantityCellProps {
  /** The server's reading, already at this seller's selling level. */
  display?: IQuantityDisplay | null;
  /** Shown when there is no reading — the stored base-unit figure. */
  fallbackBaseUnits?: number | string | null;
}

/**
 * MED-697 — a quantity in a table cell.
 *
 * Shows one figure: the count at the level this seller transacts at. "4 Cartons"
 * for a manufacturer, "9 Packs" for a distributor who breaks bulk. Nothing else.
 * Spelling the whole chain out ("4 Cartons + 1 Pack + 2 Tablets") in every row
 * of a grid was more than anyone reads at a glance, and the remainder is wanted
 * occasionally rather than always.
 *
 * Detail lives where detail already lives — the row navigates to the batch view,
 * and a batch row opens its serial tree. This cell deliberately has no expander
 * of its own: a second click target inside a row that is itself clickable is a
 * trap, and the app already had a way through to the detail.
 *
 * The full chain is on hover, though. Once the base-unit total stopped being
 * printed beside the reading, a remainder below the selling level had nowhere
 * else to appear at all, and a tooltip is the cheapest way to keep it reachable
 * without putting it back in the column.
 */
export function QuantityCell({ display, fallbackBaseUnits }: IQuantityCellProps) {
  const primary = display?.highest;
  const parts = display?.parts ?? [];

  // No reading to show — a product with no packaging hierarchy, or a response
  // from a deploy that predates this field. The stored figure is the honest
  // answer; the alternative is a blank cell where a quantity used to be.
  if (!primary) {
    return <span>{formatQuantity(display, fallbackBaseUnits)}</span>;
  }

  const headline = formatQuantityPart(primary.packaging_type, primary.quantity);
  // Only worth a tooltip when the chain says something the headline does not.
  const hasRemainder = parts.length > 1;

  return (
    <span title={hasRemainder ? formatQuantity(display, fallbackBaseUnits) : undefined}>
      {headline}
    </span>
  );
}
